import { useState } from 'react';
import { fetchAutenticado, ErrorApi } from '../api/cliente';
import type { ReporteCarga } from '../api/tipos';
import { Dialogo, Error } from '../componentes/comunes';
import { folioOT } from '../folio';

/**
 * Carga masiva de órdenes, desde un CSV o desde el Excel del cliente.
 *
 * Dos pasos deliberados. Primero se revisa el archivo sin escribir nada y
 * se enseña qué renglón entra, cuál no y por qué; sólo después se carga.
 * El paso de revisión es la mitad del valor: sin él, un archivo de
 * doscientos renglones con tres errores es «algo falló», y con él es
 * «corrige el 40, el 51 y el 77».
 *
 * La diferencia entre error y aviso también es deliberada. Un error
 * descarta ese renglón; un aviso lo deja pasar y queda anotado. Que una
 * marca no esté en el catálogo no puede impedir registrar una falla.
 */

async function enviar(archivo: File, simular: boolean): Promise<ReporteCarga> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);

  const respuesta = await fetchAutenticado(`/api/v1/ordenes/carga?simular=${simular}`, {
    method: 'POST',
    body: cuerpo,
  });

  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      String(datos.codigo_negocio ?? 'error'),
      String(datos.detail ?? 'No se pudo leer el archivo.'),
    );
  }

  return datos as ReporteCarga;
}

export default function CargaMasiva({
  onCerrar, onCargado,
}: {
  onCerrar: () => void;
  onCargado: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [reporte, setReporte] = useState<ReporteCarga | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function ejecutar(simular: boolean) {
    if (!archivo) return;
    setError(null);
    setTrabajando(true);
    try {
      const resultado = await enviar(archivo, simular);
      setReporte(resultado);
      if (!simular) onCargado();
    } catch (e) {
      setError(e);
      setReporte(null);
    } finally {
      setTrabajando(false);
    }
  }

  const yaCargado = reporte !== null && !reporte.simulacion;

  return (
    <Dialogo
      titulo="Cargar órdenes desde un archivo"
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>
            {yaCargado ? 'Cerrar' : 'Cancelar'}
          </button>

          {!yaCargado && (
            <>
              <button
                onClick={() => void ejecutar(true)}
                disabled={!archivo || trabajando}
              >
                {trabajando ? 'Revisando…' : 'Revisar'}
              </button>

              {/* Cargar sólo después de revisar: el botón no aparece hasta
                  que se sabe cuántas órdenes van a entrar. */}
              {reporte && reporte.validas > 0 && (
                <button
                  className="primario"
                  onClick={() => void ejecutar(false)}
                  disabled={trabajando}
                >
                  {trabajando
                    ? 'Cargando…'
                    : `Cargar ${reporte.validas} ${reporte.validas === 1 ? 'orden' : 'órdenes'}`}
                </button>
              )}
            </>
          )}
        </>
      }
    >
      <Error error={error} />

      {!yaCargado && (
        <div className="campo">
          <label htmlFor="archivo">Archivo</label>
          <input
            id="archivo"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setArchivo(e.target.files?.[0] ?? null);
              setReporte(null);
              setError(null);
            }}
          />
          <span className="ayuda">
            Se aceptan dos archivos: el <strong>Excel que se descarga del
            sistema del cliente</strong>, tal cual, sin quitarle columnas ni
            renglones de portada; o un <strong>CSV</strong>. Lo único
            indispensable es la determinante de la sucursal y el número de
            la orden. Las columnas que no se usan se ignoran, y la falla
            queda en blanco si el archivo no la trae.
          </span>
        </div>
      )}

      {reporte && <Reporte reporte={reporte} />}
    </Dialogo>
  );
}

function Reporte({ reporte }: { reporte: ReporteCarga }) {
  const errores = reporte.problemas.filter((p) => p.gravedad === 'error');
  const avisos = reporte.problemas.filter((p) => p.gravedad === 'aviso');

  return (
    <>
      {reporte.simulacion ? (
        <p>
          <strong>{reporte.total}</strong> renglones leídos.{' '}
          <strong>{reporte.validas}</strong> se pueden cargar
          {reporte.rechazadas > 0 && <> y <strong>{reporte.rechazadas}</strong> no</>}.
        </p>
      ) : (
        <p>
          Se cargaron <strong>{reporte.creadas?.length ?? 0}</strong> órdenes
          de {reporte.total} renglones.
        </p>
      )}

      {/*
        Qué le pasó al programa preventivo con esta carga.

        El mes del programa lo llena esto, así que quien carga el archivo
        tiene que ver el resultado aquí y no ir a otra pantalla a
        comprobarlo. Sobre todo el último renglón: una OT que no se pudo
        enlazar es el único caso que necesita ojos humanos.
      */}
      {reporte.programa && (
        reporte.programa.enlazadas > 0
        || reporte.programa.creadas > 0
      ) && (
        <div className={reporte.programa.segundasDelMes > 0 ? 'aviso' : 'ayuda'}>
          <strong>Programa preventivo:</strong>{' '}
          {reporte.programa.creadas > 0 && (
            <>{reporte.programa.creadas} visita(s) nueva(s) en el mes. </>
          )}
          {reporte.programa.enlazadas > 0 && (
            <>
              {reporte.programa.enlazadas} se enlazaron con su visita
              {reporte.programa.conOsEsperando > 0 && (
                <>, {reporte.programa.conOsEsperando} de ellas ya acusadas con
                  su OS —el técnico había ido antes—</>
              )}
              .{' '}
            </>
          )}
          {/*
            La tienda con dos OT del cliente.

            Entran las dos, porque es lo que llegó, pero se dicen aparte:
            son las únicas del lote que necesitan que alguien decida algo.
          */}
          {reporte.programa.segundasDelMes > 0 && (
            <>
              De ellas, {reporte.programa.segundasDelMes} son una segunda orden
              para una sucursal que ya tenía la suya este mes. Entran igual
              —es lo que mandó el cliente— pero revísalas en el programa y
              rechaza la que sobre.
            </>
          )}
        </div>
      )}

      {reporte.filaEncabezado > 1 && (
        <p className="ayuda">
          El encabezado se leyó del renglón {reporte.filaEncabezado}; los
          anteriores venían vacíos.
        </p>
      )}

      {errores.length > 0 && (
        <>
          <h3>Renglones que no entran</h3>
          <TablaProblemas problemas={errores} />
        </>
      )}

      {avisos.length > 0 && (
        <>
          <h3>Avisos</h3>
          <p className="ayuda">
            Estos renglones sí se cargan. La determinante es la que manda; lo
            demás se compara contra el sistema y se anota cuando no cuadra.
          </p>
          <TablaProblemas problemas={avisos} />
        </>
      )}

      {reporte.creadas && reporte.creadas.length > 0 && (
        <>
          <h3>Órdenes creadas</h3>
          <table>
            <thead><tr><th>Renglón</th><th>Folio</th></tr></thead>
            <tbody>
              {reporte.creadas.map((c) => (
                <tr key={c.folio}>
                  <td className="numero">{c.renglon}</td>
                  <td className="mono">{folioOT(c.folio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function TablaProblemas({ problemas }: { problemas: ReporteCarga['problemas'] }) {
  return (
    <table>
      <thead>
        <tr><th className="numero">Renglón</th><th>Columna</th><th>Motivo</th></tr>
      </thead>
      <tbody>
        {problemas.map((p, i) => (
          <tr key={`${p.renglon}-${p.columna}-${i}`}>
            <td className="numero">{p.renglon}</td>
            <td>{p.columna ?? '—'}</td>
            <td>{p.mensaje}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
